/**
 * UsersAccountsTab — iter-267 (Feb 2026)
 * =======================================
 * Unified accounts list — SAME 3-button layout for both platforms
 * (merchants + CloudGM customers). Every row exposes exactly three
 * actions on the left side of the card:
 *
 *   🛠️ إدارة الحساب    → navigates to the account admin page
 *   🚫 حظر / فك حظر    → opens the block-reason modal (or unblocks)
 *   📦 أرشفة            → disabled until the account is blocked
 *
 * Delete lives ONLY inside the archive page — never here, never in the
 * admin page. Lifecycle enforced by the backend proxy:
 *   block → archive → delete
 *   unblock is disabled while archived (restore first).
 *
 * NOTE: This tab renders ONLY the merchant list. The CloudGM customers
 * tab uses `CloudGMUsersTab.jsx` (same 3-button layout, own data
 * source) — see the sibling file.
 */
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, User, Plus, Mail, Phone, Loader2,
  Ban, RotateCcw, Search, ShieldCheck, Archive, X, Globe,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../../components/ui/dialog';
import { getAuthHeaders } from '../../../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersAccountsTab({
  language,
  platformInfo,
  users,
  loading,
  userStatusFilter,
  setUserStatusFilter,
  setShowAddUserModal,
  statusBadge,
  onRefresh,
}) {
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState('');
  const [blockingUser, setBlockingUser] = useState(null);   // user opened in block-reason modal
  const [blockReasonId, setBlockReasonId] = useState('');   // selected dropdown reason id
  const [blockOtherNotes, setBlockOtherNotes] = useState('');   // free-text notes when "Other" selected
  const [blockOtherNotesEn, setBlockOtherNotesEn] = useState(''); // AI-translated english notes
  const [translating, setTranslating] = useState(false);
  const [banReasons, setBanReasons] = useState([]);         // loaded from dropdown-reasons API

  // Fetch merchant ban reasons once (or when modal opens for the first time)
  useEffect(() => {
    if (!blockingUser || banReasons.length > 0) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/dropdown-reasons/merchant_ban_reason`,
          { headers: getAuthHeaders() });
        setBanReasons((r.data?.data || []).filter((x) => x.is_active !== false));
      } catch (err) {
        // Silent — fall back to Other-only if fetch fails
        setBanReasons([]);
      }
    })();
  }, [blockingUser, banReasons.length]);

  const selectedReason = banReasons.find((r) => r.id === blockReasonId);
  const isOtherSelected = !!(selectedReason && selectedReason.is_locked);

  // iter-270 — Bi-directional AI translate. If Arabic has text → translate
  // to English. Otherwise use the English text to fill the Arabic box.
  const translateOtherNotes = async () => {
    const ar = blockOtherNotes.trim();
    const en = blockOtherNotesEn.trim();
    const direction = ar ? 'ar_to_en' : (en ? 'en_to_ar' : null);
    if (!direction) {
      toast.error(t('اكتب الملاحظة أولاً', 'Enter a note first'));
      return;
    }
    setTranslating(true);
    try {
      const r = await axios.post(`${API}/translate`,
        { text: direction === 'ar_to_en' ? ar : en, direction },
        { headers: getAuthHeaders() });
      if (r.data?.translated) {
        if (direction === 'ar_to_en') setBlockOtherNotesEn(r.data.translated);
        else setBlockOtherNotes(r.data.translated);
        toast.success(t('تمت الترجمة', 'Translated'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setTranslating(false);
    }
  };

  // iter-269 — optimistic overlay on top of the server-provided `users`.
  // After a block/unblock/archive we mutate this map so the row reflects
  // the new state *immediately*, even before the parent's refetch
  // returns. Prevents the "block succeeded but archive says block first"
  // race the operator was hitting.
  const [rowOverride, setRowOverride] = useState({});   // { userId: { status, is_banned, ban_reason, archived } }

  const applyOverlay = (u) => {
    const ov = rowOverride[u.id];
    return ov ? { ...u, ...ov } : u;
  };

  const q = search.trim().toLowerCase();
  const overlaidUsers = users.map(applyOverlay);
  const filteredUsers = overlaidUsers.filter((u) => {
    // Hide archived rows immediately after archive() succeeds
    if (u.archived === true) return false;
    if (userStatusFilter !== 'all' && u.status !== userStatusFilter) return false;
    if (!q) return true;
    const fields = [u.email, u.name, u.full_name, u.first_name, u.last_name, u.phone, u.mobile];
    return fields.some((f) => (f || '').toLowerCase().includes(q));
  });

  // ─── Actions ─────────────────────────────────────────────────────
  const openBlockModal = (user) => {
    setBlockingUser(user);
    setBlockReasonId('');
    setBlockOtherNotes('');
    setBlockOtherNotesEn('');
  };

  const submitBlock = async () => {
    if (!selectedReason) {
      toast.error(t('اختر سبب الحظر', 'Select a reason'));
      return;
    }
    // If "Other" is chosen, notes are required in AT LEAST one language.
    if (isOtherSelected && !blockOtherNotes.trim() && !blockOtherNotesEn.trim()) {
      toast.error(t('اكتب الملاحظات بالعربية أو الإنجليزية', 'Enter notes in Arabic or English'));
      return;
    }
    // Display-reason falls back sensibly:
    //   Arabic note (preferred) → English note → dropdown label.
    const noteAr = blockOtherNotes.trim();
    const noteEn = blockOtherNotesEn.trim();
    const reason = isOtherSelected
      ? (noteAr || noteEn)
      : (selectedReason.label_ar || selectedReason.label_en || '');
    setBusy(blockingUser.id);
    try {
      await axios.post(
        `${API}/admin/merchant-users/${blockingUser.id}/block`,
        {
          reason,
          email: blockingUser.email,
          reason_id: selectedReason.id,
          reason_key: selectedReason.key || (selectedReason.is_locked ? 'other' : ''),
          reason_label_ar: selectedReason.label_ar,
          reason_label_en: selectedReason.label_en,
          note_ar: isOtherSelected ? noteAr : '',
          note_en: isOtherSelected ? noteEn : '',
          // Back-compat alias — older backends read `notes_en`
          notes_en: isOtherSelected ? noteEn : '',
        },
        { headers: getAuthHeaders() },
      );
      // iter-269 — optimistic overlay so the archive button unlocks NOW.
      setRowOverride((prev) => ({
        ...prev,
        [blockingUser.id]: { ...(prev[blockingUser.id] || {}), status: 'banned', is_banned: true, ban_reason: reason },
      }));
      toast.success(t('تم حظر الحساب', 'Account blocked'));
      setBlockingUser(null);
      setBlockReasonId('');
      setBlockOtherNotes('');
      setBlockOtherNotesEn('');
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy('');
    }
  };

  const unblockAccount = async (user) => {
    if (!window.confirm(t(`إلغاء حظر ${user.email}؟`, `Unblock ${user.email}?`))) return;
    setBusy(user.id);
    try {
      await axios.post(
        `${API}/admin/merchant-users/${user.id}/unblock`,
        {},
        { headers: getAuthHeaders() },
      );
      setRowOverride((prev) => ({
        ...prev,
        [user.id]: { ...(prev[user.id] || {}), status: 'active', is_banned: false, ban_reason: '' },
      }));
      toast.success(t('تم إلغاء الحظر', 'Account unblocked'));
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy('');
    }
  };

  const archiveAccount = async (user) => {
    // iter-269 — no more client-side "banned?" gate. The backend
    // enforces the rule; if it rejects we show the exact error. This
    // fixes the race where a fresh block() had not yet propagated to
    // the list state when the operator clicked archive.
    if (!window.confirm(t(`نقل ${user.email} إلى الأرشيف؟`, `Move ${user.email} to archive?`))) return;
    setBusy(user.id);
    try {
      await axios.post(
        `${API}/admin/merchant-users/${user.id}/archive`,
        {},
        { headers: getAuthHeaders() },
      );
      // Row disappears from main list immediately.
      setRowOverride((prev) => ({
        ...prev,
        [user.id]: { ...(prev[user.id] || {}), archived: true },
      }));
      toast.success(t('تمت الأرشفة', 'Archived'));
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div data-testid="users-accounts-tab">
      {/* Header + search + status filter + add button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Users className="h-6 w-6 text-[#d4af37]" />
          {platformInfo?.userLabel || t('إدارة الحسابات', 'Accounts')}
          <span className="text-sm font-normal text-[#71717A]">({users.length})</span>
        </h2>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A] pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('ابحث بالبريد/الاسم/الهاتف...', 'Search by email/name/phone...')}
              data-testid="users-search-input"
              className="bg-[#1a1a2e] border-white/10 text-white text-sm pe-9 w-64"
              dir={isAr ? 'rtl' : 'ltr'}
            />
          </div>
          <select
            value={userStatusFilter}
            onChange={(e) => setUserStatusFilter(e.target.value)}
            className="px-4 py-2 bg-[#1a1a2e] border border-white/10 rounded-xl text-white text-sm focus:border-[#d4af37] focus:outline-none cursor-pointer [&>option]:bg-[#1a1a2e] [&>option]:text-white"
            data-testid="user-status-filter"
          >
            <option value="all">{t('كل الحالات', 'All Status')}</option>
            <option value="active">{t('فعال', 'Active')}</option>
            <option value="banned">{t('محظور', 'Banned')}</option>
          </select>

          <Button
            onClick={() => setShowAddUserModal(true)}
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
            data-testid="add-user-btn"
          >
            <Plus className="h-4 w-4 me-2" />
            {t('إضافة حساب', 'Add Account')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={`u-skel-${i}`} className="h-24 bg-white/[0.02] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-16 text-[#71717A] bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>
            {q
              ? t(`لا توجد نتائج للبحث "${search}"`, `No results for "${search}"`)
              : t('لا يوجد حسابات', 'No accounts found')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map((user, idx) => {
            const isBlocked = user.status === 'banned' || user.is_banned;
            const rowBusy = busy === user.id;
            const rowKey = user.id || user.email || `row-${idx}`;
            return (
              <motion.div
                key={`${rowKey}-${user.platform || 'row'}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 hover:border-[#d4af37]/30 transition-colors"
                data-testid={`user-row-${rowKey}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  {/* Right side (RTL): user identity */}
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border shrink-0 ${
                      isBlocked
                        ? 'bg-red-500/10 border-red-500/30'
                        : 'bg-[#d4af37]/10 border-[#d4af37]/20'
                    }`}>
                      <User className={`h-6 w-6 ${isBlocked ? 'text-red-400' : 'text-[#d4af37]'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-white truncate">{user.name || t('بدون اسم', 'No name')}</p>
                        {statusBadge ? statusBadge(user.status || 'active') : null}
                      </div>
                      <p className="text-sm text-[#71717A] flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        <span dir="ltr" className="truncate">{user.email}</span>
                      </p>
                      {user.phone && (
                        <p className="text-xs text-[#71717A] flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <span dir="ltr">{user.phone}</span>
                        </p>
                      )}
                      {user.ban_reason && (
                        <p className="text-xs text-red-400 mt-1 truncate">
                          {t('سبب الحظر:', 'Ban reason:')} {user.ban_reason}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Left side (RTL): exactly 3 unified actions */}
                  <div className="flex items-center gap-2 shrink-0 flex-wrap md:flex-nowrap md:justify-end">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/manage-platform/merchant-users/${user.id || ''}?email=${encodeURIComponent(user.email || '')}`)}
                      className="bg-[#d4af37]/15 hover:bg-[#d4af37]/25 text-[#d4af37] border border-[#d4af37]/30"
                      data-testid={`manage-account-btn-${rowKey}`}
                    >
                      <ShieldCheck className="h-3.5 w-3.5 me-1" />
                      {t('إدارة الحساب', 'Manage')}
                    </Button>

                    <Button
                      size="sm"
                      onClick={() => isBlocked ? unblockAccount(user) : openBlockModal(user)}
                      disabled={rowBusy}
                      className={isBlocked
                        ? 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30'
                        : 'bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30'}
                      data-testid={`block-btn-${rowKey}`}
                    >
                      {rowBusy ? (
                        <Loader2 className="h-3.5 w-3.5 me-1 animate-spin" />
                      ) : isBlocked ? (
                        <RotateCcw className="h-3.5 w-3.5 me-1" />
                      ) : (
                        <Ban className="h-3.5 w-3.5 me-1" />
                      )}
                      {isBlocked ? t('إلغاء الحظر', 'Unblock') : t('حظر', 'Block')}
                    </Button>

                    <Button
                      size="sm"
                      onClick={() => archiveAccount(user)}
                      disabled={rowBusy || !isBlocked}
                      title={!isBlocked ? t('احظر الحساب أولاً', 'Block the account first') : ''}
                      className={isBlocked
                        ? 'bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30'
                        : 'bg-white/[0.02] text-white/30 border border-white/10 cursor-not-allowed'}
                      data-testid={`archive-btn-${rowKey}`}
                    >
                      {rowBusy ? <Loader2 className="h-3.5 w-3.5 me-1 animate-spin" /> : <Archive className="h-3.5 w-3.5 me-1" />}
                      {t('أرشفة', 'Archive')}
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Block-reason modal — iter-269 dropdown + Other free-text + translate */}
      <Dialog open={!!blockingUser} onOpenChange={(o) => { if (!o) { setBlockingUser(null); setBlockReasonId(''); setBlockOtherNotes(''); setBlockOtherNotesEn(''); } }}>
        <DialogContent
          className="bg-[#0f0f1e] border-[#2a2a4e] text-white max-w-md"
          dir={isAr ? 'rtl' : 'ltr'}
          data-testid="block-reason-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-400" />
              {t('حظر الحساب', 'Block Account')}
            </DialogTitle>
            <DialogDescription className="text-sm text-white/60">
              {t(
                `اختر سبب حظر ${blockingUser?.email || ''} من القائمة. إذا اخترت "أخرى" اكتب الملاحظات بالعربي ثم يمكنك ترجمتها.`,
                `Pick a reason for blocking ${blockingUser?.email || ''}. If you pick "Other" enter free-text notes and optionally auto-translate them.`,
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-[11px] text-white/60 block">{t('السبب', 'Reason')}</label>
            <select
              value={blockReasonId}
              onChange={(e) => setBlockReasonId(e.target.value)}
              className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-red-400/60 focus:outline-none [&>option]:bg-[#0a0a15]"
              dir={isAr ? 'rtl' : 'ltr'}
              data-testid="block-reason-select"
            >
              <option value="" disabled>{t('— اختر سبباً —', '— Choose a reason —')}</option>
              {banReasons.map((r) => (
                <option key={r.id} value={r.id}>{isAr ? r.label_ar : (r.label_en || r.label_ar)}</option>
              ))}
            </select>
            {isOtherSelected && (
              <div className="space-y-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.03] p-3">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] text-amber-300">{t('ملاحظات (يظهر للمستخدم)', 'Notes (shown to the user)')}</label>
                  <Button size="sm" variant="ghost" onClick={translateOtherNotes}
                          disabled={translating || (!blockOtherNotes.trim() && !blockOtherNotesEn.trim())}
                          className="h-6 px-2 text-[10px] text-cyan-300 hover:bg-cyan-500/10"
                          data-testid="block-other-translate-btn"
                          title={t('ترجم النص المكتوب — من العربي للإنجليزي أو العكس', 'Translate whichever box has text')}>
                    {translating ? <Loader2 className="h-3 w-3 animate-spin me-1" /> : <Globe className="h-3 w-3 me-1" />}
                    {t('ترجم بالذكاء الاصطناعي', 'AI Translate')}
                  </Button>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-white/50 flex items-center gap-1">
                    <span className="inline-block w-3 h-3 text-center text-[9px] leading-3 rounded-sm bg-emerald-500/20 text-emerald-300 font-semibold">ع</span>
                    {t('بالعربية', 'Arabic')}
                  </label>
                  <textarea
                    value={blockOtherNotes}
                    onChange={(e) => setBlockOtherNotes(e.target.value.slice(0, 500))}
                    rows={3}
                    placeholder={t('اكتب سبب الحظر بالعربية…', 'اكتب سبب الحظر بالعربية…')}
                    className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-amber-400/60 focus:outline-none"
                    dir="rtl"
                    data-testid="block-other-notes-ar"
                  />
                  <p className="text-[10px] text-white/40 text-end">{blockOtherNotes.length}/500</p>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-white/50 flex items-center gap-1">
                    <span className="inline-block w-3 h-3 text-center text-[9px] leading-3 rounded-sm bg-sky-500/20 text-sky-300 font-semibold">EN</span>
                    {t('بالإنجليزية', 'English')}
                  </label>
                  <textarea
                    value={blockOtherNotesEn}
                    onChange={(e) => setBlockOtherNotesEn(e.target.value.slice(0, 500))}
                    rows={3}
                    placeholder={t('Reason in English…', 'Reason in English…')}
                    className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-cyan-400/60 focus:outline-none"
                    dir="ltr"
                    data-testid="block-other-notes-en"
                  />
                  <p className="text-[10px] text-white/40 text-end">{blockOtherNotesEn.length}/500</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setBlockingUser(null); setBlockReasonId(''); setBlockOtherNotes(''); setBlockOtherNotesEn(''); }}
              className="border-white/10 text-white/70"
              data-testid="block-cancel-btn"
            >
              <X className="h-3.5 w-3.5 me-1" />
              {t('إلغاء', 'Cancel')}
            </Button>
            <Button
              onClick={submitBlock}
              disabled={!blockReasonId || (isOtherSelected && !blockOtherNotes.trim() && !blockOtherNotesEn.trim()) || !!busy}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="block-submit-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 me-1 animate-spin" /> : <Ban className="h-3.5 w-3.5 me-1" />}
              {t('تأكيد الحظر', 'Confirm Block')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
